import React from "react";
import { Box, Button, TextField, Typography, Checkbox, FormControlLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import headerLogo from "../assets/images/headerlogo.svg";
import Trainer from "./Trainer";
import { sessionIsOnboarded } from "../utils/onboardingStatus";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";

const COLORS = {
  bg: "#121212",
  panel: "#1a1a1a",
  border: "#2e2e2e",
  text: "#e0e0e0",
  muted: "#9a9a9a",
  green: "#56ECCF",
  gold: "#B99E4F",
  white: "#ffffff",
};

const SITES = [
  { id: "ggpoker", label: "GGPoker" },
  { id: "pokerstars", label: "PokerStars" },
  { id: "partypoker", label: "PartyPoker" },
  { id: "coinpoker", label: "Coin Poker" },
  { id: "888", label: "888" },
  { id: "wpn", label: "WPN" },
  { id: "acr", label: "ACR" },
  { id: "clubgg", label: "ClubGG" },
  { id: "live", label: "Live / casino" },
  { id: "other", label: "Other" },
];

const REVENUE_BANDS = [
  { id: "0", label: "$0 / break-even" },
  { id: "1_500", label: "$1-$500" },
  { id: "500_2k", label: "$500-$2k" },
  { id: "2k_5k", label: "$2k-$5k" },
  { id: "5k_plus", label: "$5k+" },
];

const ONLINE_STAKES = ["25NL", "50NL", "100NL", "200NL", "500NL+"];
const LIVE_STAKES = ["200NL", "500NL", "1000NL", "2000NL", "5000NL"];

const WHY_OPTIONS = [
  { id: "become_pro", label: "Become a pro" },
  { id: "money_on_side", label: "Make money on the side" },
  { id: "just_curious", label: "Just curious" },
];

const EXPECT_OPTIONS = [
  { id: "better_decisions", label: "Better decisions" },
  { id: "study_gto", label: "Study GTO" },
  { id: "track_progress", label: "Track progress" },
  { id: "beat_stakes", label: "Beat higher stakes" },
  { id: "hand_review", label: "Review hands" },
  { id: "fun_practice", label: "Fun practice" },
];

const DISCOVERY_OPTIONS = [
  { id: "google", label: "Google" },
  { id: "ai_search", label: "AI Search" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "youtube", label: "Youtube" },
  { id: "reddit", label: "Reddit" },
  { id: "email", label: "Email" },
  { id: "other", label: "Other" },
];

const ONBOARDING_TRAINER_DRILL = {
  street: "flop",
  potType: "any",
  heroPosition: "any",
  relPosition: "any",
  hands: [],
  timebank: 0,
  pauseMode: "never",
  sessionLength: 3,
  analysisMode: "during",
};

const chipSx = (selected) => ({
  textTransform: "none",
  fontSize: 14,
  fontWeight: selected ? 700 : 600,
  minHeight: 44,
  px: 1.8,
  py: 1,
  borderRadius: "10px",
  borderWidth: 1.5,
  borderColor: selected ? COLORS.gold : "#4a4a4a",
  backgroundColor: selected ? "rgba(185,158,79,0.22)" : "rgba(255,255,255,0.03)",
  color: selected ? COLORS.gold : COLORS.text,
  "&:hover": {
    borderColor: COLORS.gold,
    backgroundColor: selected ? "rgba(185,158,79,0.3)" : "rgba(185,158,79,0.1)",
  },
});

function formatStakesText(online, live) {
  const parts = [];
  if (online.length) parts.push(`Online: ${online.join(", ")}`);
  if (live.length) parts.push(`Live: ${live.join(", ")}`);
  return parts.join(" | ");
}

function parseStakesText(raw) {
  const online = [];
  const live = [];
  const text = String(raw || "");
  const onlineMatch = text.match(/Online:\s*([^|]+)/i);
  const liveMatch = text.match(/Live:\s*([^|]+)/i);
  if (onlineMatch) {
    onlineMatch[1].split(",").forEach((s) => {
      const v = s.trim();
      if (ONLINE_STAKES.includes(v)) online.push(v);
    });
  }
  if (liveMatch) {
    liveMatch[1].split(",").forEach((s) => {
      const v = s.trim();
      if (LIVE_STAKES.includes(v)) live.push(v);
    });
  }
  return { online, live };
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [name, setName] = React.useState("");
  const [playSites, setPlaySites] = React.useState([]);
  const [onlineStakes, setOnlineStakes] = React.useState([]);
  const [liveStakes, setLiveStakes] = React.useState([]);
  const [analyseSummary, setAnalyseSummary] = React.useState(null);
  const [skippedAnalyse, setSkippedAnalyse] = React.useState(false);
  const [monthlyRevenue, setMonthlyRevenue] = React.useState("");
  const [whySignup, setWhySignup] = React.useState("");
  const [expectations, setExpectations] = React.useState([]);
  const [discoverySource, setDiscoverySource] = React.useState("");
  const [discoveryOther, setDiscoveryOther] = React.useState("");

  const bounceToWelcome = React.useCallback(async (logout = false) => {
    if (logout) {
      try {
        await fetch(`${SOLVER_API_URL}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        });
      } catch (_) {}
    }
    navigate("/welcome", { replace: true });
  }, [navigate]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SOLVER_API_URL}/api/auth/session`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        // Do not bounce to login here - that caused a live login loop.
        if (!res.ok || !(data?.success || data?.verified)) return;
        if (sessionIsOnboarded(data)) {
          navigate("/", { replace: true });
          return;
        }
        if (data?.name) setName(String(data.name));
        const ob = data?.onboarding || {};
        if (Array.isArray(ob.playSites)) setPlaySites(ob.playSites);
        if (ob.stakesText) {
          const parsed = parseStakesText(ob.stakesText);
          setOnlineStakes(parsed.online);
          setLiveStakes(parsed.live);
        }
        if (ob.monthlyRevenue) setMonthlyRevenue(String(ob.monthlyRevenue));
        if (ob.whySignup) setWhySignup(String(ob.whySignup));
        if (Array.isArray(ob.expectations)) setExpectations(ob.expectations);
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const toggleSite = (id) => {
    setPlaySites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(0, 12)));
  };
  const toggleOnlineStake = (id) => {
    setOnlineStakes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleLiveStake = (id) => {
    setLiveStakes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleExpect = (id) => {
    setExpectations((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(0, 6)));
  };

  const stakesText = React.useMemo(() => formatStakesText(onlineStakes, liveStakes), [onlineStakes, liveStakes]);

  const savePartial = async (payload) => {
    const res = await fetch(`${SOLVER_API_URL}/api/auth/onboarding`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401 || res.status === 404) {
      await bounceToWelcome(true);
      throw new Error(data?.error || "Session expired.");
    }
    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || "Could not save.");
    }
    return data;
  };

  const canNext = () => {
    if (step === 0) return String(name || "").trim().length >= 2 && playSites.length > 0;
    if (step === 1) return skippedAnalyse || !!analyseSummary;
    if (step === 2) return !!monthlyRevenue;
    if (step === 3) return !!whySignup && expectations.length > 0;
    if (step === 4) {
      if (!discoverySource) return false;
      if (discoverySource === "other" && String(discoveryOther || "").trim().length < 3) return false;
      return true;
    }
    return false;
  };

  const goNext = async () => {
    setError("");
    if (step === 0) {
      setBusy(true);
      try {
        await savePartial({ name: String(name).trim(), playSites, stakesText });
        setAnalyseSummary(null);
        setSkippedAnalyse(false);
        setStep(1);
      } catch (_) {
        setError("Could not save.");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === 1) {
      setBusy(true);
      try {
        const analysePlay = skippedAnalyse
          ? { skipped: true, correct: 0, total: 0, answers: [] }
          : {
              skipped: false,
              correct: Number(analyseSummary?.correct || 0),
              total: Number(analyseSummary?.total || 0),
              answers: Array.isArray(analyseSummary?.log)
                ? analyseSummary.log.slice(0, 40).map((row, i) => ({
                    id: String(row?.street || `d${i}`),
                    choice: String(row?.action || ""),
                    correct: row?.outcome === "correct",
                  }))
                : [],
            };
        await savePartial({ analysePlay });
        setStep(2);
      } catch (_) {
        setError("Could not save.");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === 2) {
      setBusy(true);
      try {
        await savePartial({ monthlyRevenue });
        setStep(3);
      } catch (_) {
        setError("Could not save.");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === 3) {
      setBusy(true);
      try {
        await savePartial({ whySignup, expectations });
        setStep(4);
      } catch (_) {
        setError("Could not save.");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === 4) {
      setBusy(true);
      try {
        const res = await fetch(`${SOLVER_API_URL}/api/auth/onboarding/complete`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: String(name).trim(),
            playSites,
            stakesText,
            monthlyRevenue,
            whySignup,
            expectations,
            discoverySource,
            discoveryOther: discoverySource === "other" ? discoveryOther : "",
            analysePlay: skippedAnalyse
              ? { skipped: true, correct: 0, total: 0, answers: [] }
              : {
                  skipped: false,
                  correct: Number(analyseSummary?.correct || 0),
                  total: Number(analyseSummary?.total || 0),
                  answers: Array.isArray(analyseSummary?.log)
                    ? analyseSummary.log.slice(0, 40).map((row, i) => ({
                        id: String(row?.street || `d${i}`),
                        choice: String(row?.action || ""),
                        correct: row?.outcome === "correct",
                      }))
                    : [],
                },
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 401 || res.status === 404) {
          await bounceToWelcome(true);
          return;
        }
        if (!res.ok || !data?.success) {
          setError(data?.error || "Could not finish onboarding.");
          return;
        }
        window.dispatchEvent(new CustomEvent("pm:billing-refresh"));
        navigate("/", { replace: true });
      } catch (_) {
        setError("Could not contact server.");
      } finally {
        setBusy(false);
      }
    }
  };

  const onAnalyseSessionComplete = React.useCallback((summary) => {
    setSkippedAnalyse(false);
    setAnalyseSummary(summary || { correct: 0, blunders: 0, total: 0, hands: 0, log: [] });
  }, []);

  const titles = [
    "About you",
    "Analyse your play",
    "Monthly results",
    "Goals",
    "How did you hear about us?",
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: COLORS.bg,
        color: COLORS.text,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: { xs: 3, sm: 5 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: step === 1 ? 980 : 560, mx: "auto", textAlign: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2.5 }}>
          <img src={headerLogo} alt="" style={{ height: 28, opacity: 0.7 }} />
          <Typography sx={{ fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 13, fontFamily: "Play, Arial, sans-serif" }}>
            PokerMoose
          </Typography>
        </Box>
        <Typography sx={{ color: COLORS.muted, fontSize: 12.5, mb: 0.6 }}>
          Step {step + 1} of 5
        </Typography>
        <Typography sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, color: COLORS.white, mb: 2.2, fontFamily: "Play, Arial, sans-serif" }}>
          {titles[step]}
        </Typography>

        <Box
          sx={{
            backgroundColor: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2.5,
            p: { xs: 2.2, sm: 2.8 },
            display: "flex",
            flexDirection: "column",
            gap: 1.6,
            alignItems: step === 1 && !analyseSummary && !skippedAnalyse ? "stretch" : "center",
            textAlign: "center",
          }}
        >
          {step === 0 && (
            <>
              <TextField
                size="small"
                label="Your name"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 60))}
                fullWidth
                sx={{
                  maxWidth: 420,
                  "& .MuiInputBase-input": { color: COLORS.white, textAlign: "center" },
                  "& .MuiInputLabel-root": { color: COLORS.muted },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                }}
              />
              <Typography sx={{ fontSize: 13, color: COLORS.muted, mt: 0.5 }}>Where do you play?</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", maxWidth: 480 }}>
                {SITES.map((s) => (
                  <Button key={s.id} variant="outlined" onClick={() => toggleSite(s.id)} sx={chipSx(playSites.includes(s.id))}>
                    {s.label}
                  </Button>
                ))}
              </Box>
              <Typography sx={{ fontSize: 13, color: COLORS.muted, mt: 0.8 }}>Online stakes</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", maxWidth: 480 }}>
                {ONLINE_STAKES.map((s) => (
                  <Button key={s} variant="outlined" onClick={() => toggleOnlineStake(s)} sx={chipSx(onlineStakes.includes(s))}>
                    {s}
                  </Button>
                ))}
              </Box>
              <Typography sx={{ fontSize: 13, color: COLORS.muted, mt: 0.8 }}>Live stakes</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", maxWidth: 480 }}>
                {LIVE_STAKES.map((s) => (
                  <Button key={`live-${s}`} variant="outlined" onClick={() => toggleLiveStake(s)} sx={chipSx(liveStakes.includes(s))}>
                    {s}
                  </Button>
                ))}
              </Box>
            </>
          )}

          {step === 1 && (
            <>
              {!skippedAnalyse && !analyseSummary ? (
                <>
                  <Typography sx={{ fontSize: 13.5, color: COLORS.muted, maxWidth: 560, mx: "auto", mb: 0.5, textAlign: "center" }}>
                    Play 3 hands and we will see where you are losing money.
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 2,
                      overflow: "hidden",
                      backgroundColor: "#121212",
                      textAlign: "left",
                    }}
                  >
                    <Trainer
                      embed
                      continuePostflopThroughRiver
                      initialDrillSettings={ONBOARDING_TRAINER_DRILL}
                      onSessionComplete={onAnalyseSessionComplete}
                    />
                  </Box>
                  <Button
                    onClick={() => {
                      setSkippedAnalyse(true);
                      setAnalyseSummary(null);
                    }}
                    sx={{ color: COLORS.muted, textTransform: "none", fontSize: 13, alignSelf: "center" }}
                  >
                    Skip for now
                  </Button>
                </>
              ) : (
                <Typography sx={{ fontSize: 15, color: COLORS.text, maxWidth: 480, mx: "auto", textAlign: "center", py: 2, fontWeight: 600 }}>
                  {skippedAnalyse
                    ? "Skipped. You can train anytime from the dashboard."
                    : `Session complete. ${Number(analyseSummary?.correct || 0)}/${Number(analyseSummary?.total || 0)} correct decisions saved to your training stats.`}
                </Typography>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <Typography sx={{ fontSize: 13.5, color: COLORS.muted }}>Current monthly poker revenue post-rake</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", maxWidth: 420 }}>
                {REVENUE_BANDS.map((b) => (
                  <Button key={b.id} variant="outlined" onClick={() => setMonthlyRevenue(b.id)} sx={{ ...chipSx(monthlyRevenue === b.id), justifyContent: "center", width: "100%" }}>
                    {b.label}
                  </Button>
                ))}
              </Box>
            </>
          )}

          {step === 3 && (
            <>
              <Typography sx={{ fontSize: 13, color: COLORS.muted }}>Why did you sign up?</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", maxWidth: 480 }}>
                {WHY_OPTIONS.map((o) => (
                  <Button key={o.id} variant="outlined" onClick={() => setWhySignup(o.id)} sx={chipSx(whySignup === o.id)}>
                    {o.label}
                  </Button>
                ))}
              </Box>
              <Typography sx={{ fontSize: 13, color: COLORS.muted, mt: 0.8 }}>What do you expect? (pick any)</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, alignItems: "flex-start", width: "100%", maxWidth: 360, mx: "auto" }}>
                {EXPECT_OPTIONS.map((o) => (
                  <FormControlLabel
                    key={o.id}
                    control={
                      <Checkbox
                        checked={expectations.includes(o.id)}
                        onChange={() => toggleExpect(o.id)}
                        sx={{ color: COLORS.muted, "&.Mui-checked": { color: COLORS.gold } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 14, color: COLORS.text }}>{o.label}</Typography>}
                  />
                ))}
              </Box>
            </>
          )}

          {step === 4 && (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, width: "100%", maxWidth: 420 }}>
                {DISCOVERY_OPTIONS.map((opt) => (
                  <Button
                    key={opt.id}
                    variant="outlined"
                    onClick={() => {
                      setDiscoverySource(opt.id);
                      if (opt.id !== "other") setDiscoveryOther("");
                    }}
                    sx={chipSx(discoverySource === opt.id)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Box>
              {discoverySource === "other" && (
                <TextField
                  size="small"
                  placeholder="Where did you find PokerMoose?"
                  value={discoveryOther}
                  onChange={(e) => setDiscoveryOther(e.target.value)}
                  fullWidth
                  sx={{
                    maxWidth: 420,
                    "& .MuiInputBase-input": { color: COLORS.white, textAlign: "center" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                  }}
                />
              )}
            </>
          )}

          {error && <Typography sx={{ color: "#ff7d7d", fontSize: 13 }}>{error}</Typography>}

          <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 1.2, width: "100%", maxWidth: 420 }}>
            <Button
              disabled={busy || step === 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              sx={{
                ...chipSx(false),
                flex: 1,
                color: COLORS.muted,
                borderColor: "#555",
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              disabled={busy || !canNext()}
              onClick={goNext}
              sx={{
                flex: 1.4,
                minHeight: 48,
                px: 2.5,
                fontSize: 15,
                fontWeight: 800,
                textTransform: "none",
                borderRadius: "10px",
                backgroundColor: COLORS.gold,
                color: "#111",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#c9b06a", boxShadow: "none" },
                "&.Mui-disabled": { opacity: 0.45, color: "#333", backgroundColor: "#5a5040" },
              }}
            >
              {busy ? "Saving..." : step === 4 ? "Start my trial" : "Continue"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
